import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { useFragment } from 'react-relay';
import { EditorCore } from '@react-editor-js/core';
import graphql from 'babel-plugin-relay/macro';

import EditorToolbar from 'components/EditorToolbar';
import EditorLanguageField from 'components/EditorLanguageField';
import SectionWithToolbar from 'components/SectionWithToolbar';

import { useCurrentLanguage } from 'lib';

import {
  EditorJsField_text$key,
  Locale,
} from '__generated__/EditorJsField_text.graphql';
import { EditorJsField_data$key } from '__generated__/EditorJsField_data.graphql';

type Props = {
  name: string;
  text: EditorJsField_text$key | null;
  data: EditorJsField_data$key;
  fromHtml?: string | null;
  label?: ReactNode;
};

type EditorContextType = {
  name: string;
  selectedLocale: Locale;
  setLocale: (locale: Locale) => void;
  register: (locale: Locale, editor: EditorCore) => void;
  editorCore: (locale: Locale) => EditorCore | null;
};

type EditorCores = {
  LT: EditorCore | null;
  EN: EditorCore | null;
};

const EditorContext = createContext<EditorContextType>({
  name: '',
  selectedLocale: 'LT',
  setLocale: () => {},
  register: (locale: Locale, editor: EditorCore) => {},
  editorCore: (locale: Locale) => null,
});

export const useEditor = () => useContext(EditorContext);

const EditorJsField: FC<Props> = ({ name, fromHtml, label, ...props }) => {
  const lang = useCurrentLanguage();
  const [selectedLocale, setSelectedLocale] = useState<Locale>(lang);

  const editors = useRef<EditorCores>({
    LT: null,
    EN: null,
  });

  const text = useFragment(
    graphql`
      fragment EditorJsField_text on Text {
        id
        blocks {
          edges {
            node {
              locale
              data
            }
          }
        }

        ...EditorToolbar_text
      }
    `,
    props.text
  );

  const data = useFragment(
    graphql`
      fragment EditorJsField_data on Query {
        ...EditorToolbar_data

        languages {
          locale

          ...EditorLanguageField_language
          ...EditorToolbar_languages
        }
      }
    `,
    props.data
  );

  const setLocale = useCallback((locale: Locale) => {
    setSelectedLocale(locale);
  }, []);

  const register = useCallback((locale: Locale, editor: EditorCore) => {
    editors.current[locale] = editor;
  }, []);

  const editorCore = useCallback(
    (locale: Locale) => editors.current[locale],
    []
  );

  return (
    <EditorContext.Provider
      value={{
        name,
        selectedLocale,
        setLocale,
        register,
        editorCore,
      }}>
      <SectionWithToolbar
        id={name}
        title={label}
        toolbar={
          <EditorToolbar languages={data.languages} text={text} data={data} />
        }>
        {data.languages.map(language => (
          <EditorLanguageField
            key={language.locale}
            language={language}
            fromHtml={language.locale === lang ? fromHtml : null}
          />
        ))}
      </SectionWithToolbar>
    </EditorContext.Provider>
  );
};

export default EditorJsField;
